import {
  type ComputedRef,
  computed,
  onBeforeUnmount,
  onMounted,
  shallowRef,
} from 'vue';
import { router } from '@/app/router';
import { payment } from '@/services/Payment';
import { sentryService } from '@/services/telemetry/sentry';
import { track, trackAsync } from '@/shared/utils/track-telemetry';

const TRACKING_FAILED = '[Sentry] Tracking failed:';

export interface VoucherValidationPublicApi {
  readonly isNavigating: boolean;
  readonly pinCode: string | number;
  readonly validationAttempts: number;
  readonly validationStartTime: number | null;
  onInvalidAttempt: () => void;
  onPinKeyboardClose: () => void;
  onPinSuccess: () => Promise<void>;
}

interface UseVoucherValidationResult {
  onInvalidAttempt: () => void;
  onPinKeyboardClose: () => void;
  onPinSuccess: () => Promise<void>;
  pinCode: ComputedRef<string | number>;
  publicApi: VoucherValidationPublicApi;
}

export function useVoucherValidation(): UseVoucherValidationResult {
  const isNavigating = shallowRef(false);
  const validationAttempts = shallowRef(0);
  const validationStartTime = shallowRef<number | null>(null);
  const pinCode = computed<string | number>(() => payment.VOUCHER_PAYMENT_CODE);

  let isUnmounted = false;

  function validationDuration(): number | null {
    return validationStartTime.value
      ? Math.round(performance.now() - validationStartTime.value)
      : null;
  }

  async function onPinSuccess(): Promise<void> {
    if (isNavigating.value || isUnmounted) {
      return;
    }

    isNavigating.value = true;
    const duration = validationDuration();

    await trackAsync(TRACKING_FAILED, () =>
      sentryService.withSpan(
        'voucher.validation.complete',
        'payment.voucher',
        async () => {
          track(TRACKING_FAILED, () => {
            sentryService.addBreadcrumb(
              'payment.voucher',
              'Voucher validation successful',
              {
                duration_ms: duration,
                attempts: validationAttempts.value + 1,
              },
              'info'
            );
          });

          if (duration !== null) {
            track(TRACKING_FAILED, () => {
              sentryService.trackMetric(
                'voucher.validation.duration',
                duration,
                'millisecond',
                { success: 'true' }
              );
            });
          }

          track(TRACKING_FAILED, () => {
            sentryService.incrementCounter('voucher.validation.success');
          });
        },
        { validation_duration_ms: duration }
      )
    );

    validationStartTime.value = null;
    if (!isUnmounted) {
      router.redirectToStep('printing');
    }
  }

  function onPinKeyboardClose(): void {
    if (isNavigating.value || isUnmounted) {
      return;
    }

    const duration = validationDuration();

    track(TRACKING_FAILED, () => {
      sentryService.addBreadcrumb(
        'payment.voucher',
        'Voucher validation cancelled - keyboard closed',
        {
          duration_ms: duration,
          attempts: validationAttempts.value,
        },
        'warning'
      );
    });

    if (validationAttempts.value > 0) {
      track(TRACKING_FAILED, () => {
        sentryService.captureMessage(
          'Voucher validation abandoned after attempts',
          {
            level: 'warning',
            tags: { component: 'PaymentVoucher' },
            extra: {
              attempts: validationAttempts.value,
              duration_ms: duration,
            },
          }
        );
      });

      track(TRACKING_FAILED, () => {
        sentryService.incrementCounter('voucher.validation.abandoned', 1, {
          had_attempts: 'true',
        });
      });
    }

    validationStartTime.value = null;
    router.redirectToStep('payment-type');
  }

  function onInvalidAttempt(): void {
    if (isNavigating.value || isUnmounted) {
      return;
    }

    validationAttempts.value += 1;

    track(TRACKING_FAILED, () => {
      sentryService.addBreadcrumb(
        'payment.voucher',
        'Invalid voucher code entry attempt',
        { attempt_number: validationAttempts.value },
        'warning'
      );
    });

    track(TRACKING_FAILED, () => {
      sentryService.incrementCounter('voucher.validation.invalid_attempt');
    });

    if (validationAttempts.value >= 3) {
      track(TRACKING_FAILED, () => {
        sentryService.captureMessage(
          'Multiple failed voucher validation attempts',
          {
            level: 'warning',
            tags: {
              component: 'PaymentVoucher',
              security_concern: 'possible_brute_force',
            },
            extra: { attempts: validationAttempts.value },
          }
        );
      });
    }
  }

  const publicApi: VoucherValidationPublicApi = {
    get isNavigating(): boolean {
      return isNavigating.value;
    },
    get pinCode(): string | number {
      return pinCode.value;
    },
    get validationAttempts(): number {
      return validationAttempts.value;
    },
    get validationStartTime(): number | null {
      return validationStartTime.value;
    },
    onInvalidAttempt,
    onPinKeyboardClose,
    onPinSuccess,
  };

  onMounted(() => {
    isUnmounted = false;
    validationStartTime.value = performance.now();
    track(TRACKING_FAILED, () => {
      sentryService.addBreadcrumb('payment', 'Voucher payment screen mounted');
    });
  });

  onBeforeUnmount(() => {
    isUnmounted = true;
    validationStartTime.value = null;
    isNavigating.value = false;
  });

  return {
    onInvalidAttempt,
    onPinKeyboardClose,
    onPinSuccess,
    pinCode,
    publicApi,
  };
}
